import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { partnerApi } from "../../../services/partnerApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";

const inputClass =
  "mt-1.5 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20";
const labelClass = "text-sm font-semibold text-gray-900";
const sectionClass =
  "rounded-2xl border border-emerald-100/90 bg-white/90 p-5 shadow-sm sm:p-6 space-y-4";
const sectionTitleClass = "text-base font-semibold text-emerald-950";
const sectionHintClass = "mt-1 text-sm text-gray-600";

const ENTITY_TYPES = [
  { value: "sole_proprietor", label: "Sole proprietor" },
  { value: "partnership", label: "Partnership" },
  { value: "private_limited", label: "Private limited" },
  { value: "llp", label: "LLP" },
  { value: "trust", label: "Trust" },
  { value: "society", label: "Society" },
  { value: "other", label: "Other" },
];

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const QUICK_LOCATIONS = [
  { city: "Rishikesh", state: "Uttarakhand" },
  { city: "Goa", state: "Goa" },
  { city: "Dharamsala", state: "Himachal Pradesh" },
  { city: "Kochi", state: "Kerala" },
  { city: "Varkala", state: "Kerala" },
  { city: "Mysuru", state: "Karnataka" },
  { city: "Pune", state: "Maharashtra" },
  { city: "Jaipur", state: "Rajasthan" },
];

const HIGHLIGHT_OPTIONS = [
  "Yoga Retreat",
  "Meditation Retreat",
  "Wellness Retreat",
  "Ayurveda Retreat",
  "Detox / Cleanse",
  "Beach Retreat",
  "Himalayan / Mountain",
  "Ashram Stay",
  "200-Hour TTC",
  "300-Hour TTC",
  "Prenatal Yoga",
  "Silent / Vipassana",
  "Sound Healing",
  "Holistic Healing",
  "Organic Meals",
  "Vegetarian / Vegan",
  "Private Rooms",
  "Beginner Friendly",
  "Digital Detox",
];

function normalizeHighlight(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function emptyLocation() {
  return { city: "", state: "" };
}

function locationsFromOrg(org) {
  const saved = Array.isArray(org.service_locations) ? org.service_locations : [];
  const cleaned = saved
    .map((loc) => ({
      city: String(loc?.city || "").trim(),
      state: String(loc?.state || "").trim(),
    }))
    .filter((loc) => loc.city || loc.state);
  if (cleaned.length) return cleaned;
  if (org.city || org.state) {
    return [{ city: org.city || "", state: org.state || "" }];
  }
  return [emptyLocation()];
}

export default function PartnerBasicsStep() {
  const { partner, refreshPartner } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    legal_name: "",
    trade_name: "",
    entity_type: "",
    whatsapp: "",
    about: "",
    highlights: [],
    customHighlights: "",
    locations: [emptyLocation()],
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!partner?.org) return;
    const o = partner.org;
    const saved = Array.isArray(o.highlights) ? o.highlights : [];
    const presets = HIGHLIGHT_OPTIONS.filter((opt) =>
      saved.some((s) => normalizeHighlight(s) === normalizeHighlight(opt))
    );
    const extras = saved.filter(
      (s) => !HIGHLIGHT_OPTIONS.some((opt) => normalizeHighlight(opt) === normalizeHighlight(s))
    );
    setForm({
      legal_name: o.legal_name || "",
      trade_name: o.trade_name || "",
      entity_type: o.entity_type || "",
      whatsapp: o.whatsapp || "",
      about: o.about || "",
      highlights: presets,
      customHighlights: extras.join(", "),
      locations: locationsFromOrg(o),
    });
  }, [partner]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateLocation(index, key, value) {
    setForm((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) =>
        i === index ? { ...loc, [key]: value } : loc
      ),
    }));
  }

  function addLocation(preset) {
    setForm((prev) => {
      const next = { city: preset?.city || "", state: preset?.state || "" };
      const exists = prev.locations.some(
        (loc) =>
          normalizeHighlight(loc.city) === normalizeHighlight(next.city) &&
          normalizeHighlight(loc.state) === normalizeHighlight(next.state) &&
          next.city
      );
      if (exists) return prev;
      const blankOnly =
        prev.locations.length === 1 &&
        !prev.locations[0].city.trim() &&
        !prev.locations[0].state.trim();
      return {
        ...prev,
        locations: blankOnly ? [next] : [...prev.locations, next],
      };
    });
  }

  function removeLocation(index) {
    setForm((prev) => {
      if (prev.locations.length <= 1) {
        return { ...prev, locations: [emptyLocation()] };
      }
      return {
        ...prev,
        locations: prev.locations.filter((_, i) => i !== index),
      };
    });
  }

  function toggleHighlight(option) {
    setForm((prev) => {
      const has = prev.highlights.includes(option);
      return {
        ...prev,
        highlights: has
          ? prev.highlights.filter((h) => h !== option)
          : [...prev.highlights, option],
      };
    });
  }

  function collectHighlights() {
    const merged = [...form.highlights];
    const extras = form.customHighlights
      .split(/[,\n]/)
      .map((h) => h.trim())
      .filter(Boolean);
    for (const item of extras) {
      if (!merged.some((h) => normalizeHighlight(h) === normalizeHighlight(item))) {
        merged.push(item);
      }
    }
    return merged;
  }

  function collectLocations() {
    return form.locations
      .map((loc) => ({
        city: loc.city.trim(),
        state: loc.state.trim(),
        country: "India",
      }))
      .filter((loc) => loc.city && loc.state);
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    const serviceLocations = collectLocations();
    if (!serviceLocations.length) {
      setError("Add at least one city and state where you operate in India.");
      return;
    }
    setSaving(true);
    try {
      await partnerApi.patchOrg({
        legal_name: form.legal_name,
        trade_name: form.trade_name,
        entity_type: form.entity_type,
        whatsapp: form.whatsapp,
        about: form.about,
        country: "India",
        city: serviceLocations[0].city,
        state: serviceLocations[0].state,
        service_locations: serviceLocations,
        highlights: collectHighlights(),
      });
      await refreshPartner();
      navigate("/partner/onboarding/source");
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  const selectedPreview = collectHighlights();

  return (
    <OnboardingStepFrame
      title="School / retreat details"
      subtitle="Tell us about your organization. You will not enter course catalogs here — our developers list programs from your website."
    >
      <form onSubmit={handleSave} className="space-y-5">
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        <section
          className={`${sectionClass} border-emerald-200 bg-gradient-to-br from-emerald-50/90 to-white ring-1 ring-emerald-100`}
        >
          <div>
            <h2 className={sectionTitleClass}>Organization name</h2>
            <p className={sectionHintClass}>
              These two names appear on your partner profile. Use the registered legal name and the
              brand guests recognize.
            </p>
          </div>
          <label className="block">
            <span className={labelClass}>Legal name</span>
            <p className="mt-0.5 text-xs text-gray-500">
              Registered business / trust / society name (as on documents).
            </p>
            <input
              required
              minLength={2}
              className={`${inputClass} border-emerald-300 bg-white text-base`}
              value={form.legal_name}
              onChange={(e) => setField("legal_name", e.target.value)}
              placeholder="e.g. Himalayan Yoga Ashram Private Limited"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Trade / brand name</span>
            <p className="mt-0.5 text-xs text-gray-500">
              Public-facing name shown to students and on listings.
            </p>
            <input
              required
              minLength={2}
              className={`${inputClass} border-emerald-300 bg-white text-base`}
              value={form.trade_name}
              onChange={(e) => setField("trade_name", e.target.value)}
              placeholder="e.g. Himalayan Yoga Ashram"
            />
          </label>
        </section>

        <section className={sectionClass}>
          <div>
            <h2 className={sectionTitleClass}>Entity type</h2>
            <p className={sectionHintClass}>How your organization is registered.</p>
          </div>
          <label className="block">
            <span className={labelClass}>Entity type</span>
            <select
              required
              className={inputClass}
              value={form.entity_type}
              onChange={(e) => setField("entity_type", e.target.value)}
            >
              <option value="">Select…</option>
              {ENTITY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </section>

        <section className={sectionClass}>
          <div>
            <h2 className={sectionTitleClass}>Where you operate in India</h2>
            <p className={sectionHintClass}>
              Add every city and state where you run retreats or programs. Multi-location schools
              can list all of them.
            </p>
          </div>

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Quick add popular destinations
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {QUICK_LOCATIONS.map((loc) => {
                const active = form.locations.some(
                  (l) =>
                    normalizeHighlight(l.city) === normalizeHighlight(loc.city) &&
                    normalizeHighlight(l.state) === normalizeHighlight(loc.state)
                );
                return (
                  <button
                    key={`${loc.city}-${loc.state}`}
                    type="button"
                    onClick={() => addLocation(loc)}
                    aria-pressed={active}
                    className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition ${
                      active
                        ? "border-emerald-600 bg-emerald-600 text-white shadow-sm"
                        : "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"
                    }`}
                  >
                    {loc.city}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            {form.locations.map((loc, index) => (
              <div
                key={`loc-${index}`}
                className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 sm:p-4"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900">
                    Location {index + 1}
                    {index === 0 ? (
                      <span className="ml-2 text-xs font-normal text-emerald-700">
                        (primary)
                      </span>
                    ) : null}
                  </p>
                  <button
                    type="button"
                    onClick={() => removeLocation(index)}
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 hover:bg-white hover:text-red-700"
                    aria-label={`Remove location ${index + 1}`}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">City / town</span>
                    <input
                      required={index === 0}
                      className={inputClass}
                      value={loc.city}
                      onChange={(e) => updateLocation(index, "city", e.target.value)}
                      placeholder="e.g. Rishikesh"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">State</span>
                    <select
                      required={index === 0}
                      className={inputClass}
                      value={loc.state}
                      onChange={(e) => updateLocation(index, "state", e.target.value)}
                    >
                      <option value="">Select state…</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => addLocation()}
            className="inline-flex items-center gap-2 rounded-lg border border-dashed border-emerald-300 bg-white px-4 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
          >
            <Plus size={16} />
            Add another location
          </button>
        </section>

        <section className={sectionClass}>
          <div>
            <h2 className={sectionTitleClass}>About your programs</h2>
            <p className={sectionHintClass}>Contact details and what guests can expect.</p>
          </div>
          <label className="block">
            <span className={labelClass}>WhatsApp (optional)</span>
            <input
              className={inputClass}
              value={form.whatsapp}
              onChange={(e) => setField("whatsapp", e.target.value)}
              placeholder="+91…"
            />
          </label>
          <label className="block">
            <span className={labelClass}>About your school / retreats</span>
            <textarea
              required
              rows={5}
              className={inputClass}
              value={form.about}
              onChange={(e) => setField("about", e.target.value)}
              placeholder="Describe your programs, locations, and what guests experience…"
            />
          </label>
          <div>
            <span className={labelClass}>Highlights (optional)</span>
            <p className="mt-1 text-xs text-gray-500">
              Tap common retreat options, or add your own below.
            </p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {HIGHLIGHT_OPTIONS.map((option) => {
                const active = form.highlights.includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleHighlight(option)}
                    aria-pressed={active}
                    className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition ${
                      active
                        ? "border-emerald-600 bg-emerald-600 text-white shadow-sm"
                        : "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
            <label className="mt-3 block">
              <span className="text-xs text-gray-600">Add your own (comma-separated)</span>
              <input
                className={inputClass}
                value={form.customHighlights}
                onChange={(e) => setField("customHighlights", e.target.value)}
                placeholder="e.g. Rishikesh ashram, Ganga aarti, Farm stay"
              />
            </label>
            {selectedPreview.length > 0 ? (
              <p className="mt-2 text-xs text-emerald-800">
                Selected: {selectedPreview.join(", ")}
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500">No highlights selected yet.</p>
            )}
          </div>
        </section>

        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save & continue"}
        </button>
      </form>
    </OnboardingStepFrame>
  );
}
